const POST_AUTH_PATH_KEY = "routebot_post_auth_path";
const JOURNEY_KEY = "routebot_start_journey";
const START_SEEN_PREFIX = "routebot_start_seen:";

// Sign-up goes through an e-mail confirmation that usually opens in another tab, so neither the
// uploaded roster nor the "come back to /start" hint can live in sessionStorage. Both are kept in
// localStorage with an expiry instead.
export const JOURNEY_TTL_MS = 2 * 24 * 60 * 60 * 1000;
export const POST_AUTH_TTL_MS = 24 * 60 * 60 * 1000;

export const START_PATH = "/start";
// Seeded /demo used to open the first service. /start is a different door (a spreadsheet).
export const DEMO_ROUTE_PATH = "/services?tab=vehicleRoutes&open=first";

const readExpiring = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.expiresAt && Date.now() > parsed.expiresAt) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeExpiring = (key, value, ttl) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ...value, expiresAt: Date.now() + ttl }));
  } catch {
    /* quota / private mode */
  }
};

export const markEnterStartExperience = () => {
  writeExpiring(POST_AUTH_PATH_KEY, { path: START_PATH }, POST_AUTH_TTL_MS);
};

export const markEnterDemoRoute = () => {
  writeExpiring(POST_AUTH_PATH_KEY, { path: DEMO_ROUTE_PATH }, POST_AUTH_TTL_MS);
};

export const consumePostAuthPath = (fallback) => {
  const entry = readExpiring(POST_AUTH_PATH_KEY);
  if (entry?.path) {
    try {
      localStorage.removeItem(POST_AUTH_PATH_KEY);
    } catch {
      /* ignore */
    }
    return entry.path;
  }
  return fallback;
};

export const peekPostAuthPath = () => readExpiring(POST_AUTH_PATH_KEY)?.path || null;

export const markStartSeen = (clientId) => {
  if (!clientId) return;
  try {
    localStorage.setItem(`${START_SEEN_PREFIX}${clientId}`, "1");
  } catch {
    /* ignore */
  }
};

export const hasSeenStart = (clientId) => {
  if (!clientId) return false;
  try {
    return localStorage.getItem(`${START_SEEN_PREFIX}${clientId}`) === "1";
  } catch {
    return false;
  }
};

const serializeJourney = (journey) => {
  if (!journey) return null;
  return {
    stage: journey.stage,
    source: journey.source,
    fileName: journey.fileName || null,
    parsed: journey.parsed || null,
    mapping: journey.mapping || null,
    preview: journey.preview || null,
    extraction: journey.extraction || null,
    interpreter: journey.interpreter || null,
    schoolName: journey.schoolName || "",
    schoolAddress: journey.schoolAddress || "",
    schoolDecision: journey.schoolDecision || "",
    maxPassengers: journey.maxPassengers || 15,
    preferBundled: Boolean(journey.preferBundled),
    selectedRouteId: journey.selectedRouteId || null,
    walkthroughRole: journey.walkthroughRole || "dispatcher",
    intent: journey.intent || null,
    intentTouched: Boolean(journey.intentTouched),
    existingMode: journey.existingMode || "skip",
    existingDecisions: journey.existingDecisions || {},
    includeRouteList: Boolean(journey.includeRouteList),
    serviceDirection: journey.serviceDirection || null,
    serviceDirectionTouched: Boolean(journey.serviceDirectionTouched),
    // Kept in the shape only so persistStartJourney can recognise (and drop) a finished roster.
    commitResult: journey.commitResult || null,
    commitError: journey.commitError || null,
    // A save that is still running on the server; the next mount watches it instead of starting another.
    commitJobId: journey.commitJobId || null,
    commitSession: journey.commitSession?.token ? { id: journey.commitSession.id, token: journey.commitSession.token } : null,
  };
};

export const persistStartJourney = (journey) => {
  const payload = serializeJourney(journey);
  if (!payload || !payload.source) {
    clearStartJourney();
    return;
  }
  // A written roster is never kept locally (see markImportFinished).
  if (payload.commitResult) {
    clearStartJourney();
    return;
  }
  writeExpiring(JOURNEY_KEY, { ...payload, savedAt: Date.now() }, JOURNEY_TTL_MS);
};

export const readStartJourney = () => {
  const entry = readExpiring(JOURNEY_KEY);
  if (entry?.commitResult) {
    // Written by an older build: the roster is already in the database, forget the local copy.
    clearStartJourney();
    return null;
  }
  if (entry) return entry;
  // Journeys saved by the previous, tab-scoped version.
  try {
    const legacy = sessionStorage.getItem(JOURNEY_KEY);
    return legacy ? JSON.parse(legacy) : null;
  } catch {
    return null;
  }
};

export const clearStartJourney = () => {
  try {
    localStorage.removeItem(JOURNEY_KEY);
    sessionStorage.removeItem(JOURNEY_KEY);
  } catch {
    /* ignore */
  }
};

// Once a roster has been written to the database the local copy of the file must go — otherwise
// every later visit to /start would replay it. What survives is a short-lived marker with the
// result (and, for a visitor, the workspace session). The page that mounts after login reads it
// and opens the right screen; it is removed when that screen is opened, not on the first read,
// so a failed login or a strict-mode remount can still recover it.
const FINISHED_KEY = "routebot_start_finished";
export const FINISHED_TTL_MS = 10 * 60 * 1000;

export const markImportFinished = ({ result, source, intent, fileName, session } = {}) => {
  if (!result) return;
  writeExpiring(
    FINISHED_KEY,
    {
      result,
      source: source || "upload",
      intent: intent || result.intent || null,
      fileName: fileName || null,
      session: session?.token ? { id: session.id, token: session.token } : null,
    },
    FINISHED_TTL_MS,
  );
};

export const peekImportFinished = () => readExpiring(FINISHED_KEY);

// Everything /start keeps in this browser about a roster: the file and any finished marker.
export const clearStartStorage = () => {
  clearStartJourney();
  try {
    localStorage.removeItem(FINISHED_KEY);
  } catch {
    /* ignore */
  }
};
